import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const FALLBACK_IMAGE = 'https://static.readdy.ai/image/38235d25ca124ed59b2cbc0dcd639844/75ae0cfee7f6c798116a0993371ba13f.jpeg';

const VIDEO_STORAGE_KEY = 'alfa_hero_video_url';

export default function HeroSection() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [showUploader, setShowUploader] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Load saved video from IndexedDB on mount
  useEffect(() => {
    const loadVideo = async () => {
      try {
        const db = await openDB();
        const tx = db.transaction('videos', 'readonly');
        const store = tx.objectStore('videos');
        const req = store.get('hero_video');
        req.onsuccess = () => {
          if (req.result) {
            const blob = req.result as Blob;
            setVideoUrl(URL.createObjectURL(blob));
          }
        };
      } catch {
        // IndexedDB not available, skip
      }
    };
    loadVideo();
  }, []);

  const openDB = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open('alfa_media', 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore('videos');
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

  const saveVideoToDB = async (blob: Blob) => {
    try {
      const db = await openDB();
      const tx = db.transaction('videos', 'readwrite');
      tx.objectStore('videos').put(blob, 'hero_video');
    } catch {
      // fallback: skip saving
    }
  };

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) return;
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setShowUploader(false);
    saveVideoToDB(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files?.[0] ?? null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files?.[0] ?? null);
  };

  const handleRemoveVideo = async () => {
    setVideoUrl(null);
    try {
      const db = await openDB();
      const tx = db.transaction('videos', 'readwrite');
      tx.objectStore('videos').delete('hero_video');
    } catch {
      // ignore
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background: video or fallback image */}
      {videoUrl ? (
        <video
          key={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center"
        >
          <source src={videoUrl} />
        </video>
      ) : (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${FALLBACK_IMAGE}')` }}
        />
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25" />

      {/* Upload / Change video button — top right corner */}
      <div className="absolute top-6 right-6 z-30 flex gap-2">
        {videoUrl && (
          <button
            onClick={handleRemoveVideo}
            className="flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold rounded-full whitespace-nowrap hover:bg-red-900/60 hover:border-red-400/40 transition-all cursor-pointer"
          >
            <i className="ri-delete-bin-line" />
            Remove Video
          </button>
        )}
        <button
          onClick={() => setShowUploader(true)}
          className="flex items-center gap-2 px-4 py-2 bg-black/50 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold rounded-full whitespace-nowrap hover:bg-white/20 transition-all cursor-pointer"
        >
          <i className="ri-upload-cloud-line" />
          {videoUrl ? 'Change Video' : 'Upload Video'}
        </button>
      </div>

      {/* Upload Modal */}
      {showUploader && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-[#1C1C1C] border border-white/15 rounded-2xl p-8 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white text-xl font-black">Upload Hero Video</h3>
              <button
                onClick={() => setShowUploader(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-white text-lg" />
              </button>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center gap-4 cursor-pointer transition-all ${
                isDragging
                  ? 'border-white/60 bg-white/10'
                  : 'border-white/20 hover:border-white/40 hover:bg-white/5'
              }`}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/10">
                <i className="ri-video-upload-line text-white text-2xl" />
              </div>
              <div className="text-center">
                <p className="text-white font-semibold text-sm mb-1">Drag &amp; drop your video here</p>
                <p className="text-white/50 text-xs">or click to browse from your computer</p>
              </div>
              <p className="text-white/35 text-xs">MP4, MOV, WebM supported</p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={handleInputChange}
            />

            <button
              onClick={() => setShowUploader(false)}
              className="mt-4 w-full py-2.5 border border-white/20 text-white/60 text-sm font-semibold rounded-full hover:bg-white/5 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Hero content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 py-32">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 mb-7">
            <i className="ri-map-pin-line text-[#7FB3D3] text-sm" />
            <span className="text-white text-sm font-medium">Wellington &amp; Hutt Valley, New Zealand</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-6 tracking-tight">
            Premium Painting<br />
            <span className="text-gray-300">&amp; Decorating</span><br />
            in Wellington
          </h1>

          <p className="text-white/85 text-lg md:text-xl leading-relaxed mb-10 max-w-xl">
            Interior, exterior and fine finish spraying — clean, reliable and built to last.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="/contact"
              onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
              className="px-8 py-4 bg-[#2D2D2D] text-white font-bold rounded-full whitespace-nowrap hover:bg-[#1a1a1a] transition-all cursor-pointer text-base text-center"
            >
              Get a Free Quote
            </a>
            <a
              href="/projects"
              onClick={(e) => { e.preventDefault(); navigate('/projects'); }}
              className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-full whitespace-nowrap hover:bg-white/10 transition-all cursor-pointer text-base flex items-center gap-2 justify-center"
            >
              View Our Work <i className="ri-arrow-right-line" />
            </a>
          </div>

          <div className="flex flex-wrap gap-10 mt-14">
            {[
              { value: '300+', label: 'Projects Completed' },
              { value: '10+', label: 'Years Experience' },
              { value: '100%', label: 'Satisfaction Guaranteed' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl font-black text-gray-300">{stat.value}</div>
                <div className="text-white/65 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-white/50 text-lg" />
      </div>
    </section>
  );
}
